import { modifier } from 'ember-modifier';
import { normalizeCompanyTheme, type CompanyTheme } from '../utils/company-theme.ts';

const properties = ['--company-primary', '--company-secondary', '--company-on-accent'] as const;

/**
 * Applies company colors to an isolated preview, or the employee document body.
 * Restores the previous inline properties and owned class when updated or destroyed;
 * mode attributes and every unrelated style remain untouched.
 */
const companyTheme = modifier(
  (
    element: HTMLElement,
    [theme]: [CompanyTheme | undefined],
    { documentRoot = false }: { documentRoot?: boolean }
  ) => {
    const target = documentRoot ? element.ownerDocument.body : element;
    const previous = properties.map((property) => ({
      property,
      value: target.style.getPropertyValue(property),
      priority: target.style.getPropertyPriority(property),
    }));
    const wasThemed = target.classList.contains('tio-company-theme');

    const normalizedTheme = theme && normalizeCompanyTheme(theme, theme.companyId);
    if (normalizedTheme) {
      target.style.setProperty('--company-primary', normalizedTheme.primaryColor);
      target.style.setProperty('--company-secondary', normalizedTheme.secondaryColor);
      target.style.setProperty('--company-on-accent', normalizedTheme.onAccent);
      target.classList.add('tio-company-theme');
    } else {
      properties.forEach((property) => target.style.removeProperty(property));
      target.classList.remove('tio-company-theme');
    }

    return () => {
      previous.forEach(({ property, value, priority }) => {
        if (value) target.style.setProperty(property, value, priority);
        else target.style.removeProperty(property);
      });
      target.classList.toggle('tio-company-theme', wasThemed);
    };
  }
);

export default companyTheme;
